import { Fragment, useMemo, useState } from 'react'
import {
  useBillers,
  useFieldExposure,
  useGrantFieldExposure,
  useRevokeFieldExposure,
} from '../../api/queries'
import { useAuthenticatedSession } from '../../auth/session'
import type { ConfigurableField, FieldExposureRule } from '../../api/types'
import { apiErrorText } from '../../bff/errorText'
import { useT, type Translate } from '../../i18n'
import { Button, DataTable, EmptyState, ServiceTypeTag, Skeleton, StatusBadge, useToast, type Column } from '../../ui'
import screen from '../screen.module.css'
import { FieldExposureNote } from './FieldExposureNote'
import { GrantModal } from './GrantModal'
import styles from './fieldexposure.module.css'

/**
 * The field exposure screen (TRD 8002, "PII & Field Exposure Policy").
 *
 * What a biller sees is a business decision, and until this screen existed it
 * was a SQL statement — which meant the control was real but only engineering
 * could operate it. The people who should decide are Ops.
 *
 * Two things are deliberately NOT here:
 *
 *  - The fields every biller already sees. There is nothing to configure about
 *    them, and putting them on screen with a permanently-ticked box invites
 *    somebody to try to untick one. The worst a mistake here can do is reveal
 *    one more field, never withdraw one a biller relies on.
 *  - Anything the API does not offer. The catalogue is fetched, never listed
 *    on this side, so a column the service considers off-limits cannot be
 *    rendered as an option by a frontend that got ahead of it.
 *
 * The screen follows the sidebar's biller selection. With a biller selected it
 * shows the rules that APPLY to that biller — its own grants plus the legacy
 * every-biller ones, because both decide what that biller sees — and this is
 * the ONLY context in which anything can be granted or revoked: every grant is
 * for exactly one biller, so the grant form fixes its target to the selection.
 * With "All billers" selected the screen is the whole policy, unfiltered and
 * read-only — the compliance view. A field every biller should see is not a
 * grant at all; it belongs in the default tier of the catalogue.
 */
export function FieldExposure() {
  const t = useT()
  const toast = useToast()
  // `canManageUsers` is the internal-role flag. Gating the fetch on it keeps a
  // pointless 403 out of the API's audit trail, and lets the refusal below say
  // what is actually true rather than "it failed to load".
  const { user, canManageUsers: isInternal } = useAuthenticatedSession()
  const { data, isLoading, isError } = useFieldExposure(isInternal)
  const billers = useBillers(isInternal)
  const grant = useGrantFieldExposure()
  const revoke = useRevokeFieldExposure()
  const [granting, setGranting] = useState<ConfigurableField | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)

  const selectedBiller = user.billerId ?? ''

  const billerName = useMemo(() => {
    const byCode = new Map((billers.data ?? []).map((b) => [b.biller_id, b.name]))
    return (code?: string) => (code ? (byCode.get(code) ?? code) : t('fieldExposure.anyBiller'))
  }, [billers.data, t])

  const rulesByField = useMemo(() => {
    const map = new Map<string, FieldExposureRule[]>()
    for (const rule of data?.rules ?? []) {
      // Scoped view: keep the rules that decide what the selected biller
      // sees. A rule naming another biller does not, and showing it here
      // would read as "this biller has that grant".
      if (selectedBiller && rule.biller_code && rule.biller_code !== selectedBiller) continue
      const list = map.get(rule.field) ?? []
      list.push(rule)
      map.set(rule.field, list)
    }
    return map
  }, [data, selectedBiller])

  async function onRevoke(rule: FieldExposureRule) {
    setBusyId(rule.id)
    try {
      await revoke.mutateAsync(rule.id)
      toast.notify('success', t('fieldExposure.revoked'))
    } catch (error) {
      toast.notify('danger', apiErrorText(t, error).text)
    } finally {
      setBusyId(null)
    }
  }

  if (!isInternal) {
    // Belt and braces with the API's own 403. Somebody who reaches this by
    // typing the URL gets a boundary, not a broken screen.
    return <EmptyState glyph="⊘" title={t('fieldExposure.forbiddenTitle')} body={t('fieldExposure.forbiddenBody')} />
  }
  if (isLoading) return <Skeleton variant="row" count={4} label={t('fieldExposure.loading')} />
  if (isError || !data) {
    return <EmptyState glyph="⚠" title={t('fieldExposure.loadFailed')} />
  }

  return (
    <section>
      <header className={screen.header}>
        <div>
          <h1 className={screen.title}>{t('fieldExposure.title')}</h1>
          <p className={screen.subtitle}>{t('fieldExposure.subtitle')}</p>
        </div>
      </header>

      <FieldExposureNote />

      {/* Information, not an alarm: nothing here is wrong. Each field name is
          its own <strong>, so the delimited list key is split and joined here
          with a localised "and" — a translator cannot put markup in a string. */}
      <div className={screen.info} role="note">
        {t('fieldExposure.defaultsNote.before')}{' '}
        {t('fieldExposure.defaultsNote.fields')
          .split('|')
          .map((name, index, all) => (
            <Fragment key={name}>
              {index > 0 && (index === all.length - 1 ? ` ${t('fieldExposure.defaultsNote.and')} ` : ', ')}
              <strong>{name}</strong>
            </Fragment>
          ))}
        {t('fieldExposure.defaultsNote.after')}
      </div>

      {/* The scope slot: which slice of the policy is on screen, and whether
          it can be edited. The name is its own element so it can be bold and
          highlighted, which is why the sentence is two keys rather than one
          interpolation. */}
      {selectedBiller ? (
        <div className={styles.scope} role="note">
          {t('fieldExposure.scopedNoteBefore')}{' '}
          <strong className={styles.scopeBiller}>{billerName(selectedBiller)}</strong>{' '}
          {t('fieldExposure.scopedNoteAfter')}
        </div>
      ) : (
        <div className={styles.scope} role="note">
          {t('fieldExposure.readOnlyNote')}
        </div>
      )}

      <div className={styles.fields}>
        {data.fields.map((field) => (
          <FieldCard
            key={field.name}
            t={t}
            field={field}
            rules={rulesByField.get(field.name) ?? []}
            selectedBiller={selectedBiller}
            billerName={billerName}
            busyId={busyId}
            onGrant={() => setGranting(field)}
            onRevoke={onRevoke}
          />
        ))}
      </div>

      {granting && selectedBiller && (
        <GrantModal
          field={granting}
          serviceTypes={data.service_types}
          granted={(rulesByField.get(granting.name) ?? [])
            .filter((r) => (r.biller_code ?? '') === selectedBiller)
            .map((r) => r.service_type ?? '')}
          billerCode={selectedBiller}
          billerLabel={billerName(selectedBiller)}
          onClose={() => setGranting(null)}
          onSubmit={async (input) => {
            await grant.mutateAsync(input)
            toast.notify('success', t('fieldExposure.granted'))
            setGranting(null)
          }}
        />
      )}
    </section>
  )
}

function FieldCard({
  t,
  field,
  rules,
  selectedBiller,
  billerName,
  busyId,
  onGrant,
  onRevoke,
}: {
  readonly t: Translate
  readonly field: ConfigurableField
  readonly rules: readonly FieldExposureRule[]
  /** Empty in the "All billers" view, which is read-only: granting and
   *  revoking need a biller context, because every grant is for exactly one
   *  biller. Non-empty ⇒ this biller's own rules can be revoked; legacy
   *  every-biller rules still render but stay untouchable — revoking one
   *  from a single biller's context would change what every OTHER biller
   *  sees. */
  readonly selectedBiller: string
  readonly billerName: (code?: string) => string
  readonly busyId: string | null
  readonly onGrant: () => void
  readonly onRevoke: (rule: FieldExposureRule) => void
}) {
  /* A field granted with no biller, service type or gateway is granted to
     everyone. That is a legitimate configuration and it is also the one most
     likely to be a mistake, so it is called out on the card rather than left
     to be inferred from three empty cells in a table. */
  const grantedToEveryone = rules.some((r) => !r.biller_code && !r.service_type)
  const canEdit = selectedBiller !== ''

  // No gateway column: grants apply on every gateway — that dimension was
  // retired with the other low-level customisations.
  const columns: ReadonlyArray<Column<FieldExposureRule>> = [
    { key: 'biller', header: t('fieldExposure.biller'), render: (r) => billerName(r.biller_code) },
    {
      key: 'service_type',
      header: t('fieldExposure.serviceType'),
      // The shared tag: the same type is the same colour here and on the
      // transactions list, with the neutral pair reserved for "Any".
      render: (r) => <ServiceTypeTag type={r.service_type ?? ''} anyLabel={t('fieldExposure.any')} />,
    },
    { key: 'note', header: t('fieldExposure.reason'), render: (r) => r.note },
    {
      key: 'by',
      header: t('fieldExposure.grantedBy'),
      render: (r) => (
        <span className={styles.provenance}>
          {r.created_by}
          <br />
          {r.created_at.slice(0, 10)}
        </span>
      ),
    },
    ...(canEdit
      ? [
          {
            key: 'actions',
            header: '',
            render: (r: FieldExposureRule) =>
              // Only the selected biller's own rules: see `selectedBiller`.
              r.biller_code === selectedBiller ? (
                <Button
                  size="sm"
                  variant="danger"
                  disabled={busyId === r.id}
                  onClick={() => onRevoke(r)}
                >
                  {t('fieldExposure.revoke')}
                </Button>
              ) : null,
          } satisfies Column<FieldExposureRule>,
        ]
      : []),
  ]

  return (
    <article className={styles.card}>
      <header className={styles.cardHeader}>
        <div>
          <h2 className={styles.cardTitle}>
            {field.label}{' '}
            <span className={screen.mono}>{field.name}</span>
          </h2>
          <p className={styles.help}>{field.help}</p>
        </div>
        <div className={styles.cardActions}>
          {field.sensitive && <StatusBadge tone="danger">{t('fieldExposure.sensitive')}</StatusBadge>}
          <StatusBadge tone={rules.length ? 'success' : 'neutral'}>
            {rules.length
              ? t('fieldExposure.grantCount', { count: String(rules.length) })
              : t('fieldExposure.withheld')}
          </StatusBadge>
          {/* An "Any" grant covers every service type: nothing left to grant
              here for this biller, and the dialog could only make a duplicate. */}
          {/* Read-only view: say where the missing Grant button went, at the
              card, where somebody looks for it (MPAY-32364). */}
          {!canEdit ? (
            <span className={styles.help}>{t('fieldExposure.pickOrganisation')}</span>
          ) : rules.some((r) => !r.service_type) ? (
            <span className={styles.help}>{t('fieldExposure.coversAll')}</span>
          ) : (
            <Button size="sm" onClick={onGrant}>
              {t('fieldExposure.grant')}
            </Button>
          )}
        </div>
      </header>

      {grantedToEveryone && (
        <p className={styles.warning} role="alert">
          {t('fieldExposure.everyoneWarning')}
        </p>
      )}

      {rules.length > 0 && <DataTable
          caption={t('fieldExposure.tableCaption', { field: field.label })}
          sortLabel={(column) => column}
          columns={columns}
          rows={rules}
          rowKey={(r) => r.id}
        />}
    </article>
  )
}
